import {
    OptimizelyContext,
    OptimizelyProvider as InnerOptimizelyProvider,
    ReactSDKClient,
    useDecision as useOptimizelyDecision,
    setLogLevel,
} from '@optimizely/react-sdk';
import { OptimizelyUserContext } from '@optimizely/optimizely-sdk/';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { OptimizelyContextInterface } from '@optimizely/react-sdk/dist/Context';
import { constVoid } from 'fp-ts/function';
import { PWC } from '~/adapters/typescript/propsWithChildren';
import { useOptionalAuthUser } from '~/contexts/Auth/AuthContext';
import {
    getForceDecisions,
    persistForceDecision,
    removeForceDecision,
    resetDecisions,
} from '~/adapters/optimizely/localStorage';
import { useSignalmanFeatureFlag } from '~/contexts/FeatureFlagSignalmanContext';
import { FullLayoutLoader } from '~/components/Layout/FullLayoutLoader';
import { Skeleton } from '@mui/material';
import { getOrSetOptimizelyEndUserId } from '~/adapters/browser/optimizelyEndUserId';

const Timeout = 1000;

interface CustomOptimizelyContextType {
    name: string;

    setForcedFeatureFlag(featureFlag: string, value: string): void;

    resetAllFeatureFlags(): void;

    resetFeatureFlag(featureFlag: string): void;
}

const CustomOptimizelyContext = createContext<CustomOptimizelyContextType>({
    name: 'default-optimizely-context',
    setForcedFeatureFlag: constVoid,
    resetAllFeatureFlags: constVoid,
    resetFeatureFlag: constVoid,
});

/**
 * Exposes current contextual optimizely client and function for forcing Feature Flags (FF)
 */
export function useOptimizely(): CustomOptimizelyContextType & { optimizely: ReactSDKClient } {
    const optimizely = useContext<OptimizelyContextInterface>(OptimizelyContext);
    const customOptimizely = useContext(CustomOptimizelyContext);
    if (!optimizely) {
        throw new Error('Have to be used inside optimizely context');
    }

    if (!optimizely.optimizely) {
        throw new Error('Optimizely is not initialized');
    }

    return {
        optimizely: optimizely.optimizely,
        ...customOptimizely,
    };
}

export function useDecision(featureFlag: string) {
    return useOptimizelyDecision(featureFlag, { autoUpdate: true });
}

// HOF to create the hook which passes a FF as a string, where TFlags is a record of TKey: variants union, where TKey is a chosen FF key
// the purpose of HOF is to type the generated function as (keyof TFlags): the corresponding TFlags values
// the generics that are passed correspond to an Optimizely Project; we use it for Accounts but could also use FFs from another Project and have it typesafe as well
export function createUseFeatureFlag<TFlags extends Record<string, string>>() {
    return function useFeatureFlag<TKey extends keyof TFlags & string>(featureFlag: TKey): TFlags[TKey] {
        // useDecision also exposes clientReady, but we don't have to handle it – we are sure it is true (app rendering is blocked until optimizely.isReady)
        // FIY we also don't have to handle decision.enabled, because variationKey is then normally populated (usually as 'off')
        const [decision] = useDecision(featureFlag);
        return decision.variationKey as TFlags[TKey];
    };
}

interface OptimizelyProviderProps {
    name: string;
    optimizely: ReactSDKClient;
    forceSharedCookieUserId?: boolean;
    useFullLayoutLoader?: boolean;
}

export function OptimizelyProvider({
    children,
    optimizely,
    name,
    forceSharedCookieUserId = false,
    useFullLayoutLoader = true,
}: PWC<OptimizelyProviderProps>) {
    const user = useOptionalAuthUser();
    const persist = persistForceDecision(name);
    const reset = resetDecisions(name);
    const remove = removeForceDecision(name);
    const collaboration = useSignalmanFeatureFlag('collaboration');
    const loader = useFullLayoutLoader ? <FullLayoutLoader devLabel="Optimizely" /> : <Skeleton height={150} />;

    // Optimizely needs a user ID to make decisions, and it is must not change during app loading!
    // when this component is used above AuthProvider, user is not yet available, so we prefer UUID from cookie, and ignore the email when it's available
    // when unauthenticated, UUID from cookie is a fallback, because user.email won't be available
    const userIdFromCookies = getOrSetOptimizelyEndUserId();
    const optimizelyEndUserId = forceSharedCookieUserId ? userIdFromCookies : user?.email ?? userIdFromCookies;

    const [isOptimizelyReady, setOptimizelyReady] = useState(false);
    useEffect(() => {
        setOptimizelyReady(optimizely.isReady()); // necessary when switching optimizely client using dropdown in flags page
        setLogLevel('warning');

        optimizely.onReady().then(() => {
            setOptimizelyReady(true);
        });
    }, [optimizely]);

    useEffect(() => {
        // expose optimizely.userContext for tracking through GTM
        // optimizely.userContext is declared in JS SDK, but not in ReactSDKClient, so we have to cast the type from the JS SDK
        const optimizelyUser = (optimizely as any).userContext as OptimizelyUserContext | null;

        // also, userContext is prepared asynchronously, and does not have a listener
        if (optimizelyUser) {
            window.optimizelyUser = optimizelyUser;
        }
    });

    const setForcedFeatureFlag = useCallback(
        (featureFlag: string, value: string) => {
            persist(featureFlag, value);
            optimizely.setForcedDecision({ flagKey: featureFlag }, { variationKey: value });
        },
        [optimizely],
    );

    const resetFeatureFlag = useCallback(
        (featureFlag: string) => {
            remove(featureFlag);
            optimizely.removeForcedDecision({ flagKey: featureFlag });
        },
        [optimizely],
    );

    const resetAllFeatureFlags = useCallback(() => {
        reset();
        optimizely.removeAllForcedDecisions();
    }, [optimizely]);

    const forcedDecisions = useMemo(() => getForceDecisions(name), [name]);

    useEffect(() => {
        Object.entries(forcedDecisions).forEach(([key, value]) =>
            optimizely.setForcedDecision({ flagKey: key }, { variationKey: value }),
        );
    }, [optimizely, forcedDecisions]);

    const customContext = useMemo<CustomOptimizelyContextType>(
        () => ({
            name,
            setForcedFeatureFlag,
            resetAllFeatureFlags,
            resetFeatureFlag,
        }),
        [name, resetAllFeatureFlags, resetFeatureFlag, setForcedFeatureFlag],
    );

    return (
        <InnerOptimizelyProvider
            optimizely={optimizely}
            timeout={Timeout}
            user={{ id: optimizelyEndUserId, attributes: { collaboration } }}
        >
            {isOptimizelyReady ? (
                <CustomOptimizelyContext.Provider value={customContext}>{children}</CustomOptimizelyContext.Provider>
            ) : (
                loader
            )}
        </InnerOptimizelyProvider>
    );
}
